<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import BannerCard from '../components/BannerCard.vue'
import BannerCarousel from '../components/BannerCarousel.vue'
import Header from '../components/Header.vue'
import { type Banner, getAllBanners } from '../services/development/productService'
import LogoAnimation from '../components/animations/LogoAnimation.vue'
import LogoBackgroundAnimation from '../components/animations/LogoBackgroundAnimation.vue'
import IconBoxClosed from '@/components/icons/IconBoxClosed.vue'
import IconWorkerService from '@/components/icons/IconWorkerService.vue'
import BrandsCarousel from '../components/BrandsCarousel.vue'

// brand logos:
import HomeAssistantLogo from '@/assets/images/brands/home_assistant_logo.png'
import ZigbeeLogo from '@/assets/images/brands/zigbee_logo.png'
import MatterLogo from '@/assets/images/brands/matter_logo.png'
import ThreadLogo from '@/assets/images/brands/thread_logo.png'
import TuyaLogo from '@/assets/images/brands/tuya_logo.png'
import IcseeLogo from '@/assets/images/brands/icsee_logo.png'
import SonoffLogo from '@/assets/images/brands/sonoff_logo.png'
import WifiLogo from '@/assets/images/brands/wifi_logo.png'
import MqttLogo from '@/assets/images/brands/mqtt_logo.webp'
import TongouLogo from '@/assets/images/brands/tongou_logo.png'
import EspressifLogo from '@/assets/images/brands/espressif_logo.webp'
import WorksGoogleHomeLogo from '@/assets/images/brands/works_with_google_home.png'
import WorksAmazonAlexaLogo from '@/assets/images/brands/works_with_amazon_alexa.png'
import SpotifyLogo from '@/assets/images/brands/spotify_logo.png'
import YoutubeMusicLogo from '@/assets/images/brands/youtube_music_logo.png'
import RaspberryPiLogo from '@/assets/images/brands/raspberry_pi_logo.png'
import LinuxLogo from '@/assets/images/brands/linux_logo.png'

const router = useRouter()
const banners = ref<Banner[]>([])

const product_brands = ref([
  { src: SonoffLogo, alt: 'Sonoff Logo' },
  { src: TongouLogo, alt: 'Tongou Logo' },
  { src: IcseeLogo, alt: 'iCSee Logo' },
  { src: RaspberryPiLogo, alt: 'Raspberry Pi Logo' }
])

const service_brands = ref([
  { src: HomeAssistantLogo, alt: 'Home Assistant Logo' },
  { src: WorksGoogleHomeLogo, alt: 'Works With Google Home Logo' },
  { src: WorksAmazonAlexaLogo, alt: 'Works With Amazon Alexa Logo' },
  { src: TuyaLogo, alt: 'Tuya Logo' },
  { src: EspressifLogo, alt: 'Espressif Logo' },
  { src: SpotifyLogo, alt: 'Spotify Logo' },
  { src: YoutubeMusicLogo, alt: 'Youtube Music Logo' }
])

const technology_brands = ref([
  { src: MatterLogo, alt: 'Matter Logo' },
  { src: ThreadLogo, alt: 'Thread Logo' },
  { src: ZigbeeLogo, alt: 'Zigbee Logo' },
  { src: WifiLogo, alt: 'Wifi Logo' },
  { src: MqttLogo, alt: 'Mqtt Logo' },
  { src: LinuxLogo, alt: 'Linux Logo' }
])

const navigateToAllProducts = () => {
  router.push({ name: 'product' })
}

const navigateToAllServices = () => {
  router.push({ name: 'service' })
}
const navigateToService1 = () => {
  router.push({ name: 'service', hash: '#section-1' })
}

onMounted(async () => {
  banners.value = await getAllBanners()
})
</script>

<template>
  <div class="intro-container">
    <div class="bg-logo-container">
      <LogoBackgroundAnimation />
    </div>
    <div class="logo-container">
      <LogoAnimation />
      <div class="core-btns">
        <button class="core-button" @click="navigateToAllProducts">
          <IconBoxClosed />PRODUTOS
        </button>

        <button class="core-button" @click="navigateToAllServices">
          <IconWorkerService />SERVIÇOS
        </button>
      </div>
    </div>
  </div>

  <Header />
  <br />

  <div class="container">
    <h2 class="title">Nossos Serviços</h2>
    <div class="smart-info">
      <div class="smart-info-content">
        <div>
          <p class="title">Smart Central</p>
          <img
            class="info-image"
            src="@/assets/images/home-info/centralizacao_de_dispositivos.png"
            alt="smart things info"
          />
        </div>
        <div>
          <h3>Centralizador de dispositivos</h3>
          <p>
            Pensando em flexibilidade, segurança e escalabilidade utilizamos tecnologias conhecidas
            do mercado para criar uma solução moderna para o gerenciamento de casas inteligentes.
          </p>
          <br />
          <h3>Controle de onde estiver</h3>
          <p>
            Utilize aplicativos e tenha acesso a todos os seus dispositivos inteligentes de onde
            estiver, seja em casa, no trabalho ou em viagem. A Smart Central permite que você
            controle e monitore seus dispositivos de forma simples e intuitiva, tudo isso com
            segurança e privacidade.
          </p>
          <br />
          <div class="end-buttons">
            <button class="info-button">ver serviços</button>
          </div>
        </div>
      </div>
    </div>
    <h3>Principais integrações compatíveis</h3>
    <BrandsCarousel :brands="service_brands" />
    <br />

    <h2 class="title">Sobre os Produtos</h2>
    <div class="smart-info">
      <div class="smart-info-content">
        <div>
          <p class="title">Smart Components</p>
          <img
            class="info-image"
            src="@/assets/images/home-info/smart_components.png"
            alt="smart things info"
          />
        </div>
        <div class="">
          <h3>Dispositivos inteligentes</h3>
          <p>
            O uso de componentes inteligentes em aplicações como smart home, smart city e nos
            maiores setores da indústrias vem crescendo exponencialmente. Hoje eles são acessíveis
            para todos nós, pensando nisso separamos alguns dos melhores produtos voltados a IoT
            (Internet of Things) que podem ser utilizados para as mais diversas soluções. Nossa
            especialidade é domótica, automação e segurança residencial, mas também temos produtos
            voltados a automação de processos industriais e comerciais simples.
          </p>
          <br />
          <h3>Tipos de componentes e dispositivos</h3>
          <p>
            São diversos os tipos de componentes e dispositivos que podem ser utilizados em uma
            Smart Central, como sensores, atuadores, câmeras de segurança, fechaduras eletrônicas,
            termostatos, lâmpadas e leds inteligentes, entre outros. Esses dispositivos podem ser
            integrados e controlados por meio de um aplicativo ou plataforma de gerenciamento,
            permitindo que o usuário tenha controle total sobre sua casa.
          </p>
          <br />
          <div class="end-buttons">
            <button class="info-button" @click="navigateToAllProducts">ver produtos</button>
          </div>
        </div>
      </div>
    </div>
    <h3>Seleção de marcas líderes utilizadas</h3>
    <BrandsCarousel :brands="product_brands" />
    <br />

    <p class="title">Tipos de Estrutura</p>
    <BannerCarousel :slides="banners">
      <template v-slot:default="{ slide }: { slide: Banner }">
        <BannerCard :image="slide.image" :title="slide.title" :description="slide.description" />
      </template>
    </BannerCarousel>
    <br />

    <h2 class="title">Inovação e Técnologia</h2>
    <div class="smart-info">
      <div class="smart-info-content">
        <div>
          <p class="title">Smart Tec</p>
          <img
            class="info-image"
            src="@/assets/images/home-info/tecnologias_utilizadas.png"
            alt="smart things info"
          />
        </div>
        <div class="">
          <h3>Faro para novidades</h3>
          <p>
            Nosso maior compromisso é entregar soluções modernas, seguras e escaláveis, nosso
            trabalho é inovar. Desta forma estamos em envolvidos em pesquisas constantes,
            descobrindo para você quais são as novas tendências para uma vida mais inteligente.
          </p>
          <br />
          <h3>Técnologia de ponta</h3>
          <p>
            Os softwares, protocolos de comunicação, integrações e o próprio sistema são
            desenvolvidos com as mais recentes tecnologias do mercado, garantindo que sua Smart
            Central esteja sempre atualizada e pronta para o futuro.
          </p>
          <br />
          <div class="end-buttons">
            <button class="info-button">saiba mais</button>
          </div>
        </div>
      </div>
    </div>

    <h3>Técnologias chave</h3>
    <BrandsCarousel :brands="technology_brands" />
    <br />

    <h2 class="title">Casos de Uso Comuns</h2>

    <div class="smart-info">
      <div class="smart-info-content">
        <div>
          <p class="title">Conforto Residencial</p>
          <img
            class="info-image"
            src="@/assets/images/home-info/residential_confort.png"
            alt="smart things info"
          />
        </div>

        <div>
          <h3>Controle total na palma da sua mão</h3>
          <p>
            Para pessoas que desejam ter mais conforto e praticidade em suas vidas, viver em
            ambientes inteligentes, com iluminação e efeitos personalizados, cenas especiais
            controladas por assitentes de voz que gerenciem seus dispositivos. Com um simples
            comando de voz como "Alexa, modo cinema" você pode transformar sua sala em um cinema,
            com luzes ajustadas, cortinas fechadas e sistema de som ativado.
          </p>
          <br />
          <h3>Simples e fácil</h3>
          <p>
            O sitema pode ser controlado pelo smartphone, tablet, smat watch, assistentes de voz,
            paineis de controle fixo e pelo computador. Suas telas são extremamente simples e
            intuitivas, permitindo que qualquer pessoa possa utilizar sem dificuldades, além disso a
            casa continua com os acionamentos físicos padrão.
          </p>
          <br />
          <div class="end-buttons">
            <button class="info-button">saiba mais</button>
          </div>
        </div>
      </div>
    </div>

    <div class="smart-info">
      <div class="smart-info-content">
        <div>
          <p class="title">Alguel e Hospedagem Inteligente</p>
          <img
            class="info-image"
            src="@/assets/images/home-info/airbnb_concept.png"
            alt="smart things info"
          />
        </div>

        <div>
          <h3>Gerencie o fluxo de hospedes</h3>
          <p>
            Ideal para quem deseja alugar seu imóvel de forma rápida e segura, a Smart Central
            oferece uma solução completa para o gerenciamento de acessos, segurança e automação de
            ambientes, tudo isso com integração com monitoramento em tempo real, criação de usuários
            temporários, entre outras funcionalidades. Controle e acompanhe os checkins, checkouts e
            acessos de usuários de forma remota, simples e intuitiva.
          </p>
          <br />
          <h3>Coanfitrião</h3>
          <p>
            O sistema permite que o proprietário tenha total liberade para gerenciar seu imóvel, se
            deseja ser o anfitrião de seu imóvel ou chamar um coanfitrião experiente para cuidar de
            tudo, seguindo os padrões da plataforma.
          </p>
          <br />
          <div class="end-buttons">
            <button class="info-button">saiba mais</button>
          </div>
        </div>
      </div>
    </div>

    <div class="smart-info">
      <div class="smart-info-content">
        <div>
          <p class="title">Monitoramento e Controle de Acesso</p>
          <img
            class="info-image"
            src="@/assets/images/home-info/monitoramento_e_acesso.png"
            alt="smart things info"
          />
        </div>

        <div>
          <h3>Veja, grave, receba alertas de suas câmeras</h3>
          <p>
            Tenha toda segurança na residência, veja quando e quem está entrando ou saindo de sua
            casa, receba alertas em tempo real, grave vídeos e tire fotos através de câmeras
            inteligentes integradas ao sistema. Diversos pontos de câmeras são suportados além de
            gravação, as câmeras oferecidas ainda possibilitam audio biderecional entre outras
            funcionalidades.
          </p>
          <br />
          <h3>Saiba tudo sobre o imóvel através de sensores inteligentes</h3>
          <p>
            Monitore os estados dos dispositivos, sensores de portas, janelas, movimento, fumaça,
            gás, temperatura, umidade e muito mais. Receba alertas em tempo real no seu celular ou
            computador quando algo fora do comum acontecer.
          </p>
          <br />
          <h3>Dados em tempo real, monitoramento, logs e mais</h3>
          <p>
            O sistema possui um dashboard completo com dados em tempo real, gráficos, logs de
            eventos e muito mais, tudo isso para que você tenha total controle sobre sua casa
            inteligente.
          </p>
          <br />
          <div class="end-buttons">
            <button class="info-button">saiba mais</button>
          </div>
        </div>
      </div>
    </div>

    <div class="smart-info">
      <div class="smart-info-content">
        <div>
          <p class="title">Valorização Imobiliária</p>
          <img
            class="info-image"
            src="@/assets/images/home-info/valorizacao_imobiliaria.png"
            alt="smart things info"
          />
        </div>

        <div>
          <h3>Para profissionais do mercado imobiliário</h3>
          <p>
            Perfeito para construtores, arquitetos, designers, corretores, síndicos e todos os
            profissionais que trabalham no ramo imobiliário, trazendo diversas vantagens para o
            empreendimento ou estabelecimento chamando a atenção de um público mais exigente para
            suas unidades, prevendo e resolvendo problemas de uma forma inteligente.
          </p>
          <br />
          <h3>Eleve seus imóveis a outro patamar de mercado</h3>
          <p>
            Hoje em dia, imóveis inteligentes são mais valorizados e desejados por um público que
            busca praticidade, conforto e segurança. Com a Smart Central, você pode oferecer uma
            solução completa de automação e controle, tornando seus serviços mais atrativos e
            competitivos no mercado.
          </p>
          <br />
          <div class="end-buttons">
            <button class="info-button">saiba mais</button>
          </div>
        </div>
      </div>
    </div>

    <div class="smart-info">
      <div class="smart-info-content">
        <div>
          <p class="title">Acesso Remoto</p>
          <img
            class="info-image"
            src="@/assets/images/home-info/acesso_remoto.png"
            alt="smart things info"
          />
        </div>

        <div>
          <h3>Gerencie tudo de onde estiver</h3>
          <p>
            Seja sua residencia, casas para hospedagem e aluguél ou um imóvel gerenciado, você pode
            controlar tudo de onde estiver e saber o que está acontecendo em tempo real. Você pode
            acessar cada Smart Central de cada imóvel de forma separada e em diversos dispositivos.
          </p>
          <br />
          <h3>Defina quando e quem pode acessar</h3>
          <p>
            O gerenciamento de contas da Smart Central e aplicativos vínculados permitem um controle
            granular de acessos, podendo criar usuários temporários, permanentes, com permissões
            específicas para cada necessidade. Desta forma pode providenciar acessos para pessoas da
            família, prestadores de serviço, hósppedes e mais de forma separada.
          </p>
          <br />
          <div class="end-buttons">
            <button class="info-button">saiba mais</button>
          </div>
        </div>
      </div>
    </div>
    <br />

    <h2 class="title">Dúvidas frequentes</h2>
    <div class="card quest-card">
      <div class="container quest-container">
        <div class="smart-info info-hover" @click="navigateToAllProducts">
          <h3>Qual a estrutura ideal para minha necessidade?</h3>
          <p>></p>
        </div>
        <div class="smart-info info-hover" @click="navigateToAllProducts">
          <h3>Posso adicionar novos dispositivos inteligentes a minha Smart Central?</h3>
          <p>></p>
        </div>
        <div class="smart-info info-hover" @click="navigateToAllProducts">
          <h3>
            É possível utilizar assistentes de voz e outros serviços como Google Assistant ou Alexa?
          </h3>
          <p>></p>
        </div>
        <div class="smart-info info-hover" @click="navigateToAllProducts">
          <h3>Quantas pessoas podem acessar a Smart Central?</h3>
          <p>></p>
        </div>
      </div>
      <div class="container quest-container">
        <div class="smart-info info-hover" @click="navigateToAllProducts">
          <h3>O que é preciso para acessar minha Smart Central de forma remota?</h3>
          <p>></p>
        </div>
        <div class="smart-info info-hover" @click="navigateToAllProducts">
          <h3>Posso utilizar a Smart Central para alugar meu imóvel ou com Airbnb?</h3>
          <p>></p>
        </div>
        <div class="smart-info info-hover" @click="navigateToAllProducts">
          <h3>Quais tipos de equipamentos são compatíveis com a Smart Central?</h3>
          <p>></p>
        </div>
        <div class="smart-info info-hover" @click="navigateToService1">
          <h3>Quais são as tecnologias utilizadas pela Smart Central?</h3>
          <p>></p>
        </div>
      </div>
    </div>
  </div>
  <br />
</template>

<style scoped>
.card {
  background-color: var(--color-shadow-background);
  border-radius: 15px;
}

.quest-card {
  width: 100%;
  display: flex;
  flex-direction: row;
}

.quest-container {
  width: 45%;
  margin-top: 0px;
}

.title {
  background-color: var(--color-shadow-border);
  width: 250px;
  text-align: center;
  border-radius: 15px;
  margin-bottom: 15px;
}

.smart-info-content {
  display: flex;
}

.info-image {
  width: 350px;
  margin-right: 15px;
  border-radius: 10px;
}

.info-hover:hover {
  cursor: pointer;
  color: aliceblue;
  background-color: var(--color-border);
}

.intro-container {
  position: relative;
  height: 340px;
  overflow: hidden;
}

.logo-container {
  position: relative;
  z-index: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
.bg-logo-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.smart-info {
  background-color: var(--color-shadow-background);
  padding: 10px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-block: 10px;
}

.info-button {
  background-color: var(--color-border);
  color: aliceblue;
  bottom: 5px;
  padding-inline: 8px;
  padding-block: 8px;
  border-radius: 50px;
  border-color: var(--color-text);
  font-size: 1rem;
  height: 40px;
  min-width: 100px;
}

.end-buttons {
  display: flex;
  flex-direction: row;
  justify-content: end;
}

.core-btns {
  margin: auto;
  margin-top: 0px;
  padding-top: 15px;
  width: auto;
  display: flex;
  flex-direction: row;
}

.core-button {
  color: aliceblue;
  background-color: var(--color-border);
  border-color: var(--color-text);
  border-radius: 10px;
  font-size: 1rem;
  height: auto;
  width: 100%;
  margin-inline: 15px;

  animation: rgbShadow 8s infinite alternate;
}
@media (max-width: 768px) {
  .intro-container {
    height: 100vh;
  }
  .smart-info-content {
    display: block;
  }
  .info-image {
    width: 100%;
    margin-right: 0px;
  }
  .quest-card {
    flex-direction: column;
  }
  .quest-container {
    width: 95%;
    margin-top: 0px;
  }
}
</style>
